import React, { useMemo, useState } from 'react';
import { Button, Drawer, Spin, Table, TableProps, theme } from 'antd';
import { TableParams } from '../../../../../../Utils/TableTypes';
import {
  useDotRequestProgressList,
  useDotRequestProgressSession,
} from '../../../../../../Hooks/TT_ELD/Users';
import { calcOrder } from '../../../../../components/core/calcOrder';

type DotProgressRow = {
  id: number;
  sessionId: string;
  deviceType: string;
  createdAt: string;
  updatedAt: string;
  message: string;
  key: React.Key;
  raw: any;
};

const getByPath = (source: any, path: string) =>
  path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), source);

const getFirstValue = (source: any, paths: string[]) => {
  for (const path of paths) {
    const value = getByPath(source, path);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
};

const formatDate = (value: any) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const tryParseJson = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const parseMessagePayload = (message: any): Record<string, any> | undefined => {
  if (!message) return undefined;
  if (typeof message === 'object' && !Array.isArray(message)) return message;
  if (typeof message !== 'string') return undefined;

  const trimmed = message.trim();
  if (!trimmed) return undefined;

  // case 1: message is a plain JSON object string
  const firstParsed = tryParseJson(trimmed);
  if (firstParsed && typeof firstParsed === 'object' && !Array.isArray(firstParsed)) {
    return firstParsed;
  }

  // case 2: message is a JSON-encoded string that contains JSON text
  if (typeof firstParsed === 'string') {
    const secondParsed = tryParseJson(firstParsed);
    if (secondParsed && typeof secondParsed === 'object' && !Array.isArray(secondParsed)) {
      return secondParsed;
    }
  }

  // case 3: fallback for escaped logs
  const normalized = trimmed.replace(/\\n/g, '').replace(/\\"/g, '"');
  const fallback = tryParseJson(normalized);
  if (fallback && typeof fallback === 'object' && !Array.isArray(fallback)) {
    return fallback;
  }

  return undefined;
};

const normalizeJsonStringsDeep = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeJsonStringsDeep(item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, normalizeJsonStringsDeep(val)]),
    );
  }

  if (typeof value === 'string') {
    const parsed = parseMessagePayload(value);
    if (parsed && typeof parsed === 'object') {
      return normalizeJsonStringsDeep(parsed);
    }
  }

  return value;
};

const isDarkColor = (color: string) => {
  if (!color) return false;

  const hexMatch = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => `${char}${char}`)
        .join('');
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance < 140;
  }

  const rgbMatch = color.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+\s*)?\)$/i,
  );
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance < 140;
  }

  return false;
};

const DotProgressDetails = ({ driverUid }: { driverUid?: string }) => {
  const { token } = theme.useToken();
  const isDarkMode = isDarkColor(token.colorBgContainer || '');
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const current = tableParams.pagination?.current ?? 1;
  const pageSize = tableParams.pagination?.pageSize ?? 10;

  const { data, isLoading, isFetching, refetch } = useDotRequestProgressList({
    page: current,
    perPage: pageSize,
    driverUid,
  });

  const {
    data: sessionData,
    isLoading: isSessionLoading,
    isFetching: isSessionFetching,
    refetch: refetchSession,
  } = useDotRequestProgressSession(selectedSessionId, detailsOpen);

  const handleTableChange: TableProps<any>['onChange'] = (pagination) => {
    setTableParams({
      pagination,
    });
  };

  const columns: object[] = [
    {
      title: 'No',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Session ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      render: (value: string) => value || '-',
    },
    {
      title: 'Device type',
      dataIndex: 'deviceType',
      key: 'deviceType',
      width: 180,
      render: (value: string) => value || '-',
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 220,
    },
    {
      title: 'Updated at',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 220,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 140,
      render: (_: unknown, row: DotProgressRow) => (
        <Button
          onClick={() => {
            if (!row.sessionId) return;
            setSelectedSessionId(row.sessionId);
            setDetailsOpen(true);
          }}
          disabled={!row.sessionId}
        >
          Details
        </Button>
      ),
    },
  ];

  const mappedData: DotProgressRow[] = useMemo(() => {
    const rows = data?.data || [];

    return rows.map((row: any, index: number) => {
      const sessionIdRaw = getFirstValue(row, ['sessionId', 'session_id', 'session.id', 'id']);
      const messagePayload = parseMessagePayload(getFirstValue(row, ['message', 'payload.message']));
      const deviceTypeRaw =
        getFirstValue(row, ['deviceType', 'device_type']) ||
        getFirstValue(messagePayload, ['deviceType', 'device_type']);
      const createdAtRaw = getFirstValue(row, ['createdAt', 'created_at', 'date']);
      const updatedAtRaw = getFirstValue(row, ['updatedAt', 'updated_at', 'finishedAt']);
      const messageRaw = getFirstValue(row, ['message', 'error', 'description']);

      return {
        id: calcOrder(current, pageSize, index),
        sessionId: sessionIdRaw ? String(sessionIdRaw) : '',
        deviceType: deviceTypeRaw ? String(deviceTypeRaw) : '-',
        createdAt: formatDate(createdAtRaw),
        updatedAt: formatDate(updatedAtRaw),
        message: messageRaw ? String(messageRaw) : '-',
        key: row?.id ?? row?.sessionId ?? `${index}-${sessionIdRaw || 'session'}`,
        raw: row,
      };
    });
  }, [data?.data, current, pageSize]);

  const sessionViewData = useMemo(() => {
    if (sessionData === undefined || sessionData === null) return null;
    return JSON.stringify(normalizeJsonStringsDeep(sessionData), null, 2);
  }, [sessionData]);

  return (
    <div className="mainPage">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button size="large" onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      <Spin size="large" spinning={isLoading || isFetching}>
        <Table
          onChange={handleTableChange}
          dataSource={mappedData}
          columns={columns}
          pagination={{
            total: data?.count,
            showSizeChanger: false,
            current,
            pageSize,
          }}
          scroll={{ x: 'max-content' }}
          sticky
        />
      </Spin>

      <Drawer
        width={760}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={`DOT Session Details${selectedSessionId ? `: ${selectedSessionId}` : ''}`}
        styles={{
          body: {
            padding: 12,
            height: 'calc(100vh - 56px)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        extra={
          <Button
            onClick={() => refetchSession()}
            disabled={!selectedSessionId || isSessionLoading || isSessionFetching}
          >
            Refresh
          </Button>
        }
      >
        <Spin size="large" spinning={isSessionLoading || isSessionFetching}>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: isDarkMode ? '#0f1115' : '#fafafa',
              color: isDarkMode ? '#f5f5f5' : '#141414',
              border: `1px solid ${isDarkMode ? '#30363d' : '#f0f0f0'}`,
              borderRadius: 8,
              padding: 12,
              margin: 0,
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            {sessionViewData || 'No details found'}
          </pre>
        </Spin>
      </Drawer>
    </div>
  );
};

export default DotProgressDetails;
